import React, { useCallback, useState } from 'react'
import Loader from 'react-loader-spinner'
import PropTypes from 'prop-types'
import CroppedImage from './CroppedImage'
import { GGButton } from '../../../shared/new_components/GGButton'

const ImageCard = ({ image, index, onDragStart, onDragOver, onDrop, onDelete, isDragging, dragOverIndex, showLoading }) => {
  const classNames = (...classes) => classes.filter(Boolean).join(' ')

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDrop={(e) => onDrop(e, index)}
      className={classNames(
        'image-card',
        isDragging && 'dragging',
        dragOverIndex === index && 'drag-over',
      )}
      >
      {image.crop ? (
        <CroppedImage
          img={image.image}
          newWidth={image.crop.width}
          newHeight={image.crop.height}
          startX={image.crop.x}
          startY={image.crop.y}
        />
      ) : (
        <img
          src={image.image}
          alt={window.I18n.t('views.golfhub.banners_new.image_alt', { index: index + 1 })}
          draggable={false}
          className='image'
        />
      )}

      {showLoading === true && (
        <span className='span glg-loading-spinner banner-loading loading-spinner'>
          <Loader type='Oval' color='#0088CE' height={30} width={40} />
        </span>
      )}

      <div className='overlay'>
        <button className='card-btn drag' tabIndex={-1}>
          <div className='btn-icon'>
            <i className='fa-regular fa-grip-lines' aria-hidden='true'></i>
          </div>
        </button>

        <div className='overlay-spacer' />

        <button
          className='card-btn delete'
          onClick={(e) => {
            e.preventDefault()
            onDelete(index)
          }}
        >
          <div className='btn-icon'>
            <i className='fa-regular fa-x' aria-hidden='true'></i>
          </div>
        </button>
      </div>
    </div>
  )
}

const DraggableItems = ({ images, setImages, deleted, setDeleted, setModalOpen, showLoading }) => {
  const [ dragIndex, setDragIndex ] = useState(null)
  const [ dragOverIndex, setDragOverIndex ] = useState(null)

  const onDragStart = useCallback((e, index) => {
    setDragIndex(index)
    e.dataTransfer.effectAllowed = 'move'
  }, [])

  const onDragOver = useCallback((e, index) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverIndex(index)
  }, [])

  const onDrop = useCallback((e, dropIndex) => {
    e.preventDefault()
    const tempArr = [ ...images ]
    setDeleted([ ...deleted, tempArr.splice(dropIndex, 1)[0]?.id ])
    setImages((prev) => {
      if (dragIndex === null || dragIndex === dropIndex) { return prev }
      const next = [ ...prev ]
      const [ moved ] = next.splice(dragIndex, 1)
      next.splice(dropIndex, 0, moved)
      return next
    })
    setDragIndex(null)
    setDragOverIndex(null)
  }, [ dragIndex ])

  const onDragEnd = useCallback(() => {
    setDragIndex(null)
    setDragOverIndex(null)
  }, [])

  const onDelete = useCallback((index) => {
    setImages((prev) => prev.filter((_, i) => i !== index))
  }, [ setImages ])

  return (
    <div className='draggable-items'>
      <div className='banners-header'>
        <div className='text-ds-form-label'>
          {window.I18n.t('views.golfhub.banners_new.new_banners')}
        </div>
        <div className='add-padding-left-8'>
          <GGButton
          usePro
          variant='ghost'
          icon='square-plus-o'
          onClick={() => setModalOpen(true)}
        />
        </div>
      </div>
      {images.length > 0 && <div className='images-container'>
        <div className='image-list' onDragEnd={onDragEnd}>
          {images.map((img, i) => (
            <ImageCard
              key={i}
              image={img}
              index={i}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDrop={onDrop}
              onDelete={onDelete}
              isDragging={dragIndex === i}
              dragOverIndex={dragOverIndex}
              showLoading={showLoading}
            />
          ))}
        </div>
      </div>}
    </div>
  )
}
DraggableItems.propTypes = {
  setModalOpen: PropTypes.func,
  setImages: PropTypes.func,
  setDeleted: PropTypes.func,
  images: PropTypes.array,
  deleted: PropTypes.array,
  showLoading: PropTypes.bool,
}

ImageCard.propTypes = {
  image: PropTypes.shape({
    image: PropTypes.string.isRequired,
    crop: PropTypes.shape({
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired,
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
    }),
  }).isRequired,
  index: PropTypes.number.isRequired,
  onDragStart: PropTypes.func.isRequired,
  onDragOver: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isDragging: PropTypes.bool,
  dragOverIndex: PropTypes.number,
  showLoading: PropTypes.bool,
}

export default DraggableItems
